import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import CardUI from "../../common/Card";
import AvatarWithName from "../../components/AvatarWithName";
import { timeDateFormat } from "../../utils/helpers";
import ReferralEmpty from "../../assets/images/referral-empty.png";
import { getReferralsListFunc } from "../../actions/Referrals";
import AlertMessage from "../../common/AlertMessage";
import { getAgencyDetailFunc } from "../../actions/Agency";
import { InfoCircle } from "../../assets/constants";
import { segmentTrack } from "../../utils/helpers/tracking_utils";
import CaseTableSkeleton from "../../components/CaseTableSkeleton";
import ReferralStatusSelect from "./ReferralStatusSelect";

const Referrals = () => {
  const dispatch = useDispatch();
  const { agentUser } = useSelector(state => state.agentProfile);
  const { loading, error, referralsList } = useSelector(state => state.getReferralsList);
  const { agencyDetail } = useSelector(state => state.getAgencyDetail);
  const [alert, setAlert] = useState(null);
  const [pageSize, setPageSize] = useState(5);
  const showFeedback = useCallback(feedback => setAlert(feedback), []);

  useEffect(() => {
    dispatch(getReferralsListFunc());
    dispatch(getAgencyDetailFunc());
  }, [dispatch]);

  useEffect(() => {
    if (agentUser?.agent && agencyDetail?.agency) {
      segmentTrack({ event: "referrals", agent: agentUser.agent, agency: agencyDetail.agency });
    }
  }, [agentUser, agencyDetail]);

  useEffect(() => {
    if (error) setAlert({ severity: "error", message: error });
  }, [error]);

  const rows = useMemo(() => (referralsList?.referrals || []).map(referral => ({
    id: referral.id,
    referrer: {
      name: referral.client?.name || "",
      profile_picture_url: referral.client?.profile_picture_url || "",
    },
    status: referral.status,
    sentDate: referral.created_at ? timeDateFormat(referral.created_at) : "",
  })), [referralsList]);

  const columns = useMemo(() => [
    {
      field: "referrer",
      headerName: "Referrer",
      minWidth: 200,
      flex: 1,
      renderCell: ({ value }) => <AvatarWithName name={value.name} imageURL={value.profile_picture_url} />,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 280,
      flex: 1,
      sortable: false,
      renderHeader: () => (
        <Box display="flex" gap="4px" alignItems="center">
          <Typography fontSize="14px" fontWeight="500">Status</Typography>
          <Tooltip title="Choose a status to track follow-up. Changes are saved automatically." placement="top" arrow>
            <InfoCircle />
          </Tooltip>
        </Box>
      ),
      renderCell: ({ id, value, row }) => (
        <ReferralStatusSelect id={id} status={value} referrerName={row.referrer.name} onFeedback={showFeedback} />
      ),
    },
    { field: "sentDate", headerName: "Created Date", sortable: false, minWidth: 180, flex: 1 },
  ], [showFeedback]);

  return (
    <>
      {alert && (
        <AlertMessage
          message={alert.message}
          severity={alert.severity}
          open
          handleClose={(event, reason) => { if (reason !== "clickaway") setAlert(null); }}
        />
      )}
      <Stack>
        <Typography variant="h5" fontSize="30px" pb={3}>Referrals</Typography>
        <CardUI maxWidth="100%" dashboard="true" responsive="true" height="792px" padding="32px">
          {loading ? (
            <CaseTableSkeleton headers={["Referrer", "Status", "Created Date"]} numRows={4} />
          ) : rows.length ? (
            <Box height="100%" width="100%">
              <DataGrid
                rows={rows}
                columns={columns}
                rowHeight={72}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                rowsPerPageOptions={[5, 10]}
                disableSelectionOnClick
                sx={{
                  border: "none",
                  [`& .${gridClasses.cell}`]: { py: "10px", display: "flex", alignItems: "center" },
                }}
              />
            </Box>
          ) : (
            <Stack height="100%" alignItems="center" justifyContent="center" gap={1}>
              <img src={ReferralEmpty} alt="referrals" />
              <Typography variant="body2" textAlign="center" color="rgba(0, 0, 0, 0.38)">
                When your clients start referring you to their friends it will start showing up here.
              </Typography>
            </Stack>
          )}
        </CardUI>
      </Stack>
    </>
  );

};

export default Referrals;
